import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { Button, Input, Select, Textarea } from "@/shared/ui";
import { updateCase } from "@/shared/db";
import { toast } from "sonner";
import type { CaseWithDetails, CaseFormData } from "../types/case.types";

interface EditCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  case_: CaseWithDetails | null;
}

export const EditCaseModal: React.FC<EditCaseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  case_,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CaseFormData>>({
    title: "",
    description: "",
    practice_area: "civil",
    priority: "medium",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof CaseFormData, string>>
  >({});

  useEffect(() => {
    if (case_) {
      setFormData({
        title: case_.title || "",
        description: case_.description || "",
        practice_area: case_.practice_area || "civil",
        priority: case_.priority || "medium",
      });
    }
  }, [case_]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CaseFormData, string>> = {};

    if (!formData.title?.trim()) {
      newErrors.title = t("lawyer.editCase.errors.titleRequired");
    }

    if (!formData.practice_area) {
      newErrors.practice_area = t(
        "lawyer.editCase.errors.practiceAreaRequired"
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!case_ || !validateForm()) {
      return;
    }

    try {
      setLoading(true);

      await updateCase(case_.id, {
        title: formData.title,
        description: formData.description || null,
        practice_area: formData.practice_area,
        priority: formData.priority,
      });

      toast.success(t("lawyer.editCase.success"));
      onSuccess();
      handleClose();
    } catch (error) {
      console.error("Error updating case:", error);
      toast.error(t("lawyer.editCase.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setErrors({});
      onClose();
    }
  };

  const practiceAreas = [
    { code: "civil", name: t("lawyer.cases.practiceAreas.civil") },
    { code: "penal", name: t("lawyer.cases.practiceAreas.penal") },
    {
      code: "laboral",
      name: t("lawyer.cases.practiceAreas.laboral"),
    },
    {
      code: "comercial",
      name: t("lawyer.cases.practiceAreas.comercial"),
    },
    { code: "fiscal", name: t("lawyer.cases.practiceAreas.fiscal") },
    {
      code: "administrativo",
      name: t("lawyer.cases.practiceAreas.administrativo"),
    },
    { code: "familiar", name: t("lawyer.practiceAreas.familiar") },
    {
      code: "inmobiliario",
      name: t("lawyer.practiceAreas.inmobiliario"),
    },
    {
      code: "propiedad_intelectual",
      name: t("lawyer.practiceAreas.propiedad_intelectual"),
    },
    {
      code: "migratorio",
      name: t("lawyer.practiceAreas.migratorio"),
    },
    {
      code: "ambiental",
      name: t("lawyer.practiceAreas.ambiental"),
    },
    {
      code: "corporativo",
      name: t("lawyer.practiceAreas.corporativo"),
    },
  ];

  if (!isOpen || !case_) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Background overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal container */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full z-10 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-tiber-700 to-tiber-500 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-white">
                  {t("lawyer.editCase.title")}
                </h3>
                <p className="text-tiber-100 mt-1 text-sm">
                  {case_.case_number} •{" "}
                  {case_.client_profiles?.profiles?.full_name || "N/A"}
                </p>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-xl disabled:opacity-50"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Title */}
            <div>
              <Input
                label={t("lawyer.editCase.title") + " *"}
                type="text"
                value={formData.title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder={t("lawyer.editCase.titlePlaceholder")}
                disabled={loading}
                className={errors.title ? "border-red-500" : ""}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            {/* Practice Area & Priority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Select
                  label={t("lawyer.editCase.practiceArea") + " *"}
                  value={formData.practice_area}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setFormData({
                      ...formData,
                      practice_area: e.target
                        .value as CaseFormData["practice_area"],
                    })
                  }
                  disabled={loading}
                  options={practiceAreas.map((area) => ({
                    value: area.code,
                    label: area.name,
                  }))}
                />
                {errors.practice_area && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.practice_area}
                  </p>
                )}
              </div>

              <div>
                <Select
                  label={t("lawyer.editCase.priority")}
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      priority: e.target.value as CaseFormData["priority"],
                    })
                  }
                  disabled={loading}
                  options={[
                    {
                      value: "low",
                      label: t("lawyer.editCase.priorities.low"),
                    },
                    {
                      value: "medium",
                      label: t("lawyer.editCase.priorities.medium"),
                    },
                    {
                      value: "high",
                      label: t("lawyer.editCase.priorities.high"),
                    },
                    {
                      value: "urgent",
                      label: t("lawyer.editCase.priorities.urgent"),
                    },
                  ]}
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-tiber-700 mb-2">
                {t("lawyer.editCase.description")}
              </label>
              <Textarea
                value={formData.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={4}
                placeholder={t("lawyer.editCase.descriptionPlaceholder")}
                disabled={loading}
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-tiber-100">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={loading}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                isLoading={loading}
              >
                {t("lawyer.editCase.update")}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};
